// Iter 81 / Phase E0 — HR Masters admin (Department + Designation).
// Single page with two stacked editable tables.
import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

function EditableMasterTable({ kind, label, entityLabel, items, reload }) {
    const { can } = useAuth();
    const [editing, setEditing] = useState(null); // either an id (for existing) or "new"
    const [draft, setDraft] = useState({});

    const start = (row) => {
        setEditing(row?.id || "new");
        setDraft(row || (kind === "designation" ? { name: "", description: "", level: 0 } : { name: "", description: "" }));
    };

    const cancel = () => { setEditing(null); setDraft({}); };

    const save = async () => {
        try {
            if (editing === "new") {
                await api.post(`/hr/${kind}`, draft);
                toast.success(`${entityLabel} created.`);
            } else {
                await api.patch(`/hr/${kind}/${editing}`, draft);
                toast.success(`${entityLabel} updated.`);
            }
            cancel();
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete ${entityLabel} "${row.name}"?`)) return;
        try {
            await api.delete(`/hr/${kind}/${row.id}`);
            toast.success(`${entityLabel} deleted.`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const isDesignation = kind === "designation";

    return (
        <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`hr-master-${kind}`}>
            <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-slate-800">{label}</h3>
                {can("employee:admin") && editing !== "new" && (
                    <Button data-testid={`hr-master-${kind}-new-btn`} size="sm" onClick={() => start(null)}><Plus size={14} className="mr-1" /> New</Button>
                )}
            </div>
            <table className="w-full text-sm" data-testid={`hr-master-${kind}-table`}>
                <thead className="bg-slate-50 text-slate-600 text-xs">
                    <tr>
                        <th className="text-left px-3 py-2 font-medium">Name</th>
                        {isDesignation && <th className="text-left px-3 py-2 font-medium w-20">Level</th>}
                        <th className="text-left px-3 py-2 font-medium">Description</th>
                        <th className="w-32" />
                    </tr>
                </thead>
                <tbody>
                    {editing === "new" && (
                        <tr className="border-t border-slate-100 bg-amber-50/40">
                            <td className="px-3 py-2"><Input data-testid={`${kind}-new-name`} value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={isDesignation ? "Senior Manager" : "Operations"} /></td>
                            {isDesignation && <td className="px-3 py-2"><Input type="number" value={draft.level || 0} onChange={(e) => setDraft({ ...draft, level: parseInt(e.target.value || "0", 10) })} className="w-20" /></td>}
                            <td className="px-3 py-2"><Input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></td>
                            <td className="px-3 py-2 text-right">
                                <Button data-testid={`${kind}-new-save`} size="sm" onClick={save}><FloppyDisk size={12} /></Button>
                                <Button size="sm" variant="ghost" onClick={cancel} className="ml-1"><X size={12} /></Button>
                            </td>
                        </tr>
                    )}
                    {(items || []).map((row) => (
                        editing === row.id ? (
                            <tr key={row.id} className="border-t border-slate-100 bg-amber-50/40">
                                <td className="px-3 py-2"><Input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
                                {isDesignation && <td className="px-3 py-2"><Input type="number" value={draft.level || 0} onChange={(e) => setDraft({ ...draft, level: parseInt(e.target.value || "0", 10) })} className="w-20" /></td>}
                                <td className="px-3 py-2"><Input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></td>
                                <td className="px-3 py-2 text-right">
                                    <Button size="sm" onClick={save}><FloppyDisk size={12} /></Button>
                                    <Button size="sm" variant="ghost" onClick={cancel} className="ml-1"><X size={12} /></Button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={row.id} className="border-t border-slate-100">
                                <td className="px-3 py-2 text-slate-800 font-medium">{row.name}</td>
                                {isDesignation && <td className="px-3 py-2 text-slate-600">{row.level}</td>}
                                <td className="px-3 py-2 text-xs text-slate-500">{row.description || "—"}</td>
                                <td className="px-3 py-2 text-right">
                                    {can("employee:admin") && (
                                        <>
                                            <button onClick={() => start(row)} className="text-slate-500 hover:text-slate-700 mr-2"><PencilSimple size={14} /></button>
                                            <button onClick={() => remove(row)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        )
                    ))}
                </tbody>
            </table>
        </section>
    );
}

export default function HrMasters() {
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const reload = useCallback(() => {
        Promise.all([
            api.get("/hr/department/list"),
            api.get("/hr/designation/list"),
        ])
            .then(([d1, d2]) => {
                setDepartments(d1.data.items || []);
                setDesignations(d2.data.items || []);
            })
            .catch((err) => toast.error(formatApiError(err, "Failed to load masters")));
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1200px]" data-testid="hr-masters-page">
            <PageHeader
                eyebrow="HR Configuration"
                title="Departments & Designations"
                description="Master data for the employee directory. Operators may extend these as the organisation evolves."
            />
            <div className="mt-6 grid grid-cols-1 gap-6">
                <EditableMasterTable kind="department" label="Departments" entityLabel="Department" items={departments} reload={reload} />
                <EditableMasterTable kind="designation" label="Designations" entityLabel="Designation" items={designations} reload={reload} />
            </div>
        </div>
    );
}
